import { useState, useMemo, useEffect } from 'react';
import { ServiceMap } from './components/Map/ServiceMap';
import { ServiceTabs } from './components/Filters/ServiceTabs';
import { CityFilter } from './components/Filters/CityFilter';
import { LocationButton } from './components/LocationButton';
import { PointsList } from './components/PointsList';
import { Loading } from './components/Loading';
import { useWebflowData, useWebflowServices, useWebflowCities } from './hooks/useWebflowData';
import { getServiceConfig } from './utils/serviceConfig';
import './App.css';

const POINTS_COLLECTION_ID = import.meta.env.VITE_WEBFLOW_COLLECTION_ID || '6785685394e6b757b6961d3e';
const SERVICES_COLLECTION_ID = '67632d56646946d607488da5';
const CITIES_COLLECTION_ID = '67709a752122796ef44c70ef';

function App() {
  const [activeService, setActiveService] = useState(null);
  const [selectedCity, setSelectedCity] = useState('');
  const [userLocation, setUserLocation] = useState(null);

  // Cargar datos de puntos, servicios y ciudades
  const { data: allPoints, loading: pointsLoading } = useWebflowData(POINTS_COLLECTION_ID);
  const { services: webflowServices, loading: servicesLoading } = useWebflowServices(SERVICES_COLLECTION_ID);
  const { cities: webflowCities, loading: citiesLoading } = useWebflowCities(CITIES_COLLECTION_ID);

  const loading = pointsLoading || servicesLoading || citiesLoading;

  // Crear mapa de ciudades por ID
  const citiesMap = useMemo(() => {
    const map = new Map();
    webflowCities.forEach(city => {
      map.set(city.id, city.name);
    });
    return map;
  }, [webflowCities]);

  // Combinar servicios de Webflow con configuración
  const availableServices = useMemo(() => {
    if (!webflowServices || webflowServices.length === 0) {
      console.error('[DEBUG] availableServices: No webflowServices', webflowServices);
      return [];
    }
    
    const services = webflowServices.map((service) => {
      const config = getServiceConfig(service.name, service.id);
      // Priorizar color de Webflow (color-2), pero verificar que no esté vacío
      // Si service.color existe y no está vacío, usarlo; si no, usar clusterColor del config, luego color por defecto
      const serviceColor = (service.color && service.color.trim() !== '') 
        ? service.color.trim() 
        : (config?.clusterColor || config?.color || '#4ECDC4');
      
      console.error('[DEBUG] availableServices: Service color resolution:', {
        serviceName: service.name,
        webflowColor: service.color,
        resolvedColor: serviceColor,
        configClusterColor: config?.clusterColor,
        order: service.order
      });
      
      return {
        serviceName: service.name,
        serviceId: service.id,
        order: service.order || 999, // Si no tiene order, ponerlo al final
        config: {
          ...config,
          icon: service.icon || config?.icon,
          color: serviceColor,
          clusterColor: serviceColor, // Usar el mismo color para clusters
          id: service.id || config?.id,
          label: service.name || config?.label,
        }
      };
    })
    .filter(item => item.config !== null)
    // Ordenar por order de menor a mayor
    .sort((a, b) => {
      const orderA = a.order || 999;
      const orderB = b.order || 999;
      return orderA - orderB;
    });
    
    console.error('[DEBUG] availableServices:', services.map(s => ({ name: s.serviceName, color: s.config.color, order: s.order })));
    return services;
  }, [webflowServices]);

  // Ciudades disponibles basadas en los puntos
  const availableCities = useMemo(() => {
    if (!allPoints || allPoints.length === 0) return [];
    
    const departmentIds = [...new Set(
      allPoints
        .map(point => point.department)
        .filter(Boolean)
    )];
    
    return departmentIds
      .map(deptId => {
        const cityName = citiesMap.get(deptId);
        if (!cityName) return null;
        const city = webflowCities.find(c => c.id === deptId);
        return city ? { id: city.id, name: city.name } : null;
      })
      .filter(Boolean)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allPoints, citiesMap, webflowCities]);

  // Establecer primer servicio como activo
  useEffect(() => {
    if (!activeService && availableServices.length > 0) {
      console.error('[DEBUG] Setting first service as active:', availableServices[0].serviceName);
      setActiveService(availableServices[0].serviceName);
    }
  }, [availableServices, activeService]);

  // Debug: Comparar servicios de puntos (IDs) con servicios disponibles (IDs)
  useEffect(() => {
    if (allPoints && allPoints.length > 0 && availableServices.length > 0) {
      const pointServiceIds = [...new Set(allPoints.map(p => p.service).filter(Boolean))];
      const availableServiceIds = availableServices.map(s => s.serviceId || s.config?.id).filter(Boolean);
      
      console.error('[DEBUG] Service Matching Analysis (by ID):');
      console.error('  - Service IDs in points:', pointServiceIds);
      console.error('  - Available service IDs:', availableServiceIds);
      console.error('  - Active service name:', activeService);
      
      // Encontrar coincidencias por ID
      const matches = pointServiceIds.filter(psId => 
        availableServiceIds.includes(psId)
      );
      console.error('  - Matches found (by ID):', matches);
      
      // También mostrar nombres para referencia
      const availableServiceNames = availableServices.map(s => ({
        name: s.serviceName,
        id: s.serviceId || s.config?.id
      }));
      console.error('  - Available services (name/id):', availableServiceNames);
    }
  }, [allPoints, availableServices, activeService]);

  // Configuración del servicio activo
  const activeConfig = useMemo(() => {
    if (!activeService) return null;
    const service = availableServices.find(
      s => s.serviceName.toLowerCase() === activeService.toLowerCase()
    );
    return service?.config || null;
  }, [activeService, availableServices]);

  // Filtrar puntos por servicio activo y ciudad seleccionada
  // IMPORTANTE: point.service y point.department son IDs, no nombres
  const filteredPoints = useMemo(() => {
    if (!activeService) {
      console.error('[DEBUG] filteredPoints: No activeService');
      return [];
    }
    
    if (!allPoints || allPoints.length === 0) {
      console.error('[DEBUG] filteredPoints: No allPoints');
      return [];
    }
    
    // Encontrar el servicio activo para obtener su ID
    const activeServiceObj = availableServices.find(
      s => s.serviceName.toLowerCase() === activeService.toLowerCase()
    );
    
    if (!activeServiceObj) {
      console.error('[DEBUG] filteredPoints: activeServiceObj not found for:', activeService);
      return [];
    }
    
    const activeServiceId = activeServiceObj.serviceId || activeServiceObj.config?.id;
    console.error('[DEBUG] filteredPoints: activeService:', activeService);
    console.error('[DEBUG] filteredPoints: activeServiceId:', activeServiceId);
    console.error('[DEBUG] filteredPoints: allPoints count:', allPoints.length);
    
    // Obtener todos los service IDs únicos de los puntos para debugging
    const uniquePointServiceIds = [...new Set(allPoints.map(p => p.service).filter(Boolean))];
    console.error('[DEBUG] filteredPoints: unique service IDs in points:', uniquePointServiceIds);
    
    const filtered = allPoints.filter(point => {
      // Comparar IDs: point.service (ID) con activeServiceId
      const serviceMatch = 
        point.service === activeServiceId ||
        point.service === activeServiceObj.serviceId ||
        point.service?.toString() === activeServiceId?.toString();
      
      // Comparar IDs: point.department (ID) con selectedCity (ID)
      const cityMatch = !selectedCity || point.department === selectedCity;
      
      if (serviceMatch && cityMatch) {
        console.error('[DEBUG] filteredPoints: Match found:', {
          pointServiceId: point.service,
          activeServiceId,
          pointDepartment: point.department,
          selectedCity,
          title: point.title
        });
      }
      
      return serviceMatch && cityMatch;
    });
    
    console.error('[DEBUG] filteredPoints: Final count:', filtered.length);
    return filtered;
  }, [allPoints, activeService, selectedCity, availableServices]);

  // Manejar click en un punto de la lista
  const handlePointClick = (point) => {
    if (!point || !activeConfig) return;
    
    const mapId = activeConfig.mapId;
    if (window.mapCenterFunctions && window.mapCenterFunctions[mapId]) {
      window.mapCenterFunctions[mapId](point);
    }
  };

  // Agregar marcador de ubicación del usuario
  useEffect(() => {
    if (!userLocation) return;

    availableServices.forEach(({ config }) => {
      if (!config) return;
      const map = window.mapInstances?.[config.mapId];
      if (map) {
        new window.google.maps.Marker({
          position: userLocation,
          map: map,
          title: "Estás acá!",
          icon: {
            url: "https://cdn.prod.website-files.com/6733bde60e89ed56a4840f90/680ed7f4e59ad688186d329a_c4503b364894c1caf8805c5dedcef637_current-location.png"
          }
        });
        map.setCenter(userLocation);
        map.setZoom(15);
      }
    });
  }, [userLocation, availableServices]);

  // Resetear filtro de ciudad al cambiar servicio
  useEffect(() => {
    setSelectedCity('');
  }, [activeService]);

  if (loading) {
    return <Loading message="Cargando puntos de servicio..." />;
  }

  if (availableServices.length === 0) {
    return <Loading message="No se encontraron servicios disponibles." />;
  }

  return (
    <div className="app-container">
      {/* Mapa en la parte superior */}
      <div className="maps-container">
        {availableServices.map(({ serviceName, config }) => {
          const isActive = activeService?.toLowerCase() === serviceName.toLowerCase();
          const pointsForService = isActive ? filteredPoints : [];
          
          return (
            <ServiceMap
              key={serviceName}
              serviceType={serviceName}
              serviceConfig={config}
              servicePoints={pointsForService}
              isVisible={isActive}
            />
          );
        })}
        {/* Elementos flotantes sobre el mapa (desktop) */}
        <div className="floating-city-filter">
          <CityFilter
            cities={availableCities}
            selectedCity={selectedCity}
            onCityChange={setSelectedCity}
          />
        </div>
        <div className="floating-location-button">
          <LocationButton onLocationFound={setUserLocation} />
        </div>
      </div>
      {/* Contenedor móvil para filtros (después del mapa) */}
      <div className="mobile-controls-row">
        <div className="mobile-city-filter">
          <CityFilter
            cities={availableCities}
            selectedCity={selectedCity}
            onCityChange={setSelectedCity}
          />
        </div>
        <div className="mobile-location-button">
          <LocationButton onLocationFound={setUserLocation} />
        </div>
      </div>
      {/* Filtros en la parte inferior */}
      <div className="filters-container">
        <ServiceTabs 
          activeService={activeService}
          onServiceChange={setActiveService}
          availableServices={availableServices}
        />
      </div>
      
      {/* Lista de puntos en la parte inferior */}
      <PointsList
        points={filteredPoints}
        activeService={activeService}
        serviceConfig={activeConfig}
        onPointClick={handlePointClick}
      />
    </div>
  );
}

export default App;
